<script setup>
// Configurações › Integrações › Asaas | Tramitação Inteligente (09/10/2026).
//
// Pedido do Victor: "eu configurei. O que que o cliente vai usar com isso?
// Isso aqui vai interligar com o quê?". A página responde em três blocos, de
// cima para baixo: COMO ESTÁ (o herói, com uma frase e o passo seguinte), COM
// O QUE LIGA (cada linha abre o módulo de verdade) e a CONFIGURAÇÃO.
//
// O estado vem do servidor (Integracoes::DoEscritorio); a chave nunca volta,
// só os 4 últimos caracteres. Associação: o mesmo herói das fichas
// (`.advup-heroi`) e a mesma linha de lista da ficha do processo.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import IntegracoesAPI from 'dashboard/api/integracoesDoEscritorio';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import EstadoVazio from 'dashboard/components-next/EstadoVazio.vue';
import EsqueletoLista from 'dashboard/components-next/EsqueletoLista.vue';
import ConectarAsaas from './ConectarAsaas.vue';
import {
  FONTE as FONTE_DAS_TARIFAS,
  CONFERIDO_EM,
  linhasDasTarifas,
  promocao,
} from './tarifasDoAsaas';
import {
  ligacoes,
  montarEnvio,
  proximoPasso,
  tomDoEstado,
} from './integracaoDoEscritorio';

const props = defineProps({
  fornecedor: { type: String, required: true },
});

const { t } = useI18n();
const route = useRoute();
const T = 'INTEGRACAO_ESCRITORIO';
const F = computed(() => `${T}.${props.fornecedor.toUpperCase()}`);
const LOGO = computed(
  () => `/dashboard/images/integrations/${props.fornecedor}.png`
);
const ehAsaas = computed(() => props.fornecedor === 'asaas');
const PERCENTUAL = '%';

const retrato = ref(null);
const carregando = ref(true);
const falha = ref(false);
const form = ref({});
const mostrarChave = ref(false);
const salvando = ref(false);
const testando = ref(false);
const conectando = ref(false);
const teste = ref(null);
// O guia de conectar o Asaas (11/10/2026): colar a chave e conectar num gesto.
const guiaAberto = ref(false);
// Onde o escritório que ainda não tem conta abre a dele. Sem acordo de tarifa
// (Victor, 11/10/2026): o cartão não promete preço.
const SITE_DO_ASAAS = 'https://www.asaas.com/';
// A tabela padrão do Asaas no cartão de quem vai abrir a conta (Victor,
// 11/10/2026: "pessoa ter mais informação").
const tarifas = linhasDasTarifas();
const promo = promocao();

// O percentual volta do servidor com ponto ("1.5"); na tela, como se escreve
// no Brasil. A volta para número é do `percentual` (aceita os dois).
const comVirgula = valor =>
  valor === null || valor === undefined ? '' : String(valor).replace('.', ',');

const preencherForm = r => {
  form.value = ehAsaas.value
    ? {
        asaas_api_key: '',
        asaas_enabled: !!r.ligada,
        default_billing_type: r.padroes?.forma || 'pix',
        default_interest_percent: comVirgula(r.padroes?.juros),
        default_fine_percent: comVirgula(r.padroes?.multa),
      }
    : {
        tramitacao_api_key: '',
        assinaturas_dono_email: r.dono_email || '',
        tramitacao_enabled: !!r.fonte_de_andamentos,
      };
};

const receber = payload => {
  retrato.value = payload;
  preencherForm(payload);
};

const carregar = async () => {
  carregando.value = true;
  falha.value = false;
  try {
    const { data } = await IntegracoesAPI.retrato(props.fornecedor);
    receber(data.payload);
  } catch {
    falha.value = true;
  } finally {
    carregando.value = false;
  }
};

const salvar = async () => {
  salvando.value = true;
  try {
    const { data } = await IntegracoesAPI.salvar(
      props.fornecedor,
      montarEnvio(props.fornecedor, form.value)
    );
    receber(data.payload);
    // O teste anterior era de outra configuração (talvez de outra chave).
    teste.value = null;
    mostrarChave.value = false;
    useAlert(t(`${T}.SALVO`));
  } catch (e) {
    const erros = e?.response?.data?.errors;
    useAlert(erros?.length ? erros.join(' ') : t(`${T}.FALHA_SALVAR`));
  } finally {
    salvando.value = false;
  }
};

const testar = async () => {
  testando.value = true;
  teste.value = null;
  try {
    const { data } = await IntegracoesAPI.testar(props.fornecedor);
    teste.value = data.teste;
    retrato.value = data.payload;
  } catch {
    teste.value = { ok: false, erro: t(`${T}.ERRO_CARREGAR_DICA`) };
  } finally {
    testando.value = false;
  }
};

const conectar = async () => {
  conectando.value = true;
  try {
    const { data } = await IntegracoesAPI.conectarAvisos(props.fornecedor);
    retrato.value = data.payload;
    useAlert(t(`${F.value}.AVISOS_OK`));
  } catch (e) {
    const erro = e?.response?.data?.resultado?.erro;
    if (e?.response?.data?.payload) retrato.value = e.response.data.payload;
    teste.value = { ok: false, erro: erro || t(`${T}.ERRO_CARREGAR_DICA`) };
  } finally {
    conectando.value = false;
  }
};

// A Tramitação não guarda a saúde no servidor: depois de um teste que falhou,
// a tela não pode continuar dizendo que está tudo certo. Assume "chave
// recusada" até a próxima chave salva (o Asaas já volta assim do servidor).
const retratoDaTela = computed(() => {
  const falhou =
    teste.value && !teste.value.ok && teste.value.erro !== 'sem_chave';
  if (!retrato.value || ehAsaas.value || !falhou) return retrato.value;
  return { ...retrato.value, estado: 'chave_recusada' };
});

const tom = computed(() => tomDoEstado(retratoDaTela.value?.estado));
const passo = computed(() => proximoPasso(retratoDaTela.value));
const lista = computed(() =>
  ligacoes(props.fornecedor, retratoDaTela.value || {})
);
const sandbox = computed(
  () => ehAsaas.value && retrato.value?.ambiente === 'sandbox'
);

// O que o teste disse, em frases. Só a exceção ganha frase própria.
const frasesDoTeste = computed(() => {
  const r = teste.value;
  if (!r) return [];
  if (!r.ok) {
    const erro =
      r.erro === 'sem_chave' ? t(`${F.value}.ESTADO.sem_chave`) : r.erro;
    return [{ tom: 'rubi', texto: t(`${F.value}.TESTE.FALHOU`, { erro }) }];
  }
  if (ehAsaas.value) {
    const frases = [{ tom: 'teal', texto: t(`${F.value}.TESTE.OK`) }];
    if (r.pode_sacar === false)
      frases.push({ tom: 'ambar', texto: t(`${F.value}.TESTE.SACAR_NAO`) });
    if (!r.avisos_conectados)
      frases.push({ tom: 'ambar', texto: t(`${F.value}.TESTE.AVISOS_NAO`) });
    return frases;
  }
  const frases = [
    { tom: 'teal', texto: t(`${F.value}.TESTE.OK`, { usuarios: r.usuarios }) },
  ];
  if (r.dono_encontrado === true)
    frases.push({ tom: 'teal', texto: t(`${F.value}.TESTE.DONO_OK`) });
  if (r.dono_encontrado === false)
    frases.push({ tom: 'ambar', texto: t(`${F.value}.TESTE.DONO_NAO`) });
  return frases;
});

const destino = ligacao => ({
  name: ligacao.rota,
  params: { accountId: route.params.accountId },
  query: ligacao.consulta || {},
});

// Sem chave ou chave recusada: o caminho é o guia, não o formulário.
const pedeGuia = computed(
  () =>
    ehAsaas.value &&
    ['sem_chave', 'chave_recusada'].includes(retratoDaTela.value?.estado)
);

const aoConectar = payload => {
  receber(payload);
  teste.value = null;
};

const quando = iso => dynamicTime(new Date(iso).getTime() / 1000);

onMounted(carregar);
watch(() => props.fornecedor, carregar);
</script>

<template>
  <div class="ide">
    <BaseSettingsHeader
      :icone="ehAsaas ? 'i-ph-receipt' : 'i-ph-signature'"
      :title="t(`${F}.TITULO`)"
      :description="t(`${F}.DESCRICAO`)"
      :back-button-label="t(`${T}.VOLTAR`)"
    />

    <EsqueletoLista v-if="carregando" :linhas="3" :altura="96" />

    <EstadoVazio
      v-else-if="falha"
      tom="erro"
      icone="i-ph-warning-circle"
      :titulo="t(`${T}.ERRO_CARREGAR_TIT`)"
      :dica="t(`${T}.ERRO_CARREGAR_DICA`)"
    >
      <button type="button" class="advup-btn reset-base" @click="carregar()">
        <i class="i-hugeicons-refresh" aria-hidden="true" />
        {{ t(`${T}.TENTAR`) }}
      </button>
    </EstadoVazio>

    <template v-else-if="retrato">
      <!-- ── Como está ─────────────────────────────────────────────────── -->
      <section class="advup-heroi ide-heroi" :class="`advup-heroi--${tom}`">
        <div class="advup-heroi__topo">
          <span class="advup-heroi__foto advup-heroi__foto--marca">
            <img :src="LOGO" alt="" />
          </span>
          <div class="advup-heroi__txt">
            <p class="advup-heroi__sup">{{ t(`${T}.SUP`) }}</p>
            <h2 class="advup-heroi__tit ide-heroi__frase">
              {{ t(`${F}.ESTADO.${retratoDaTela.estado}`) }}
            </h2>
            <div class="advup-heroi__selos">
              <span
                v-if="retrato.chave_final"
                class="advup-heroi__selo ficha-pill ficha-pill--neutro"
              >
                {{ t(`${T}.CHAVE_FINAL`, { final: retrato.chave_final }) }}
              </span>
              <!-- O ambiente sempre escrito (Fase 2): só "Sandbox" aparecia, e
                   a ausência dele não dizia "produção" para quem não sabia.
                   Mas só com chave gravada (T17, 13/10/2026): antes dela,
                   "Ambiente de produção" não diz nada a quem vai conectar. -->
              <span
                v-if="sandbox"
                class="advup-heroi__selo ficha-pill ficha-pill--ambar"
              >
                {{ t(`${T}.SANDBOX`) }}
              </span>
              <span
                v-else-if="
                  ehAsaas &&
                  retrato.chave_final &&
                  retrato.ambiente === 'production'
                "
                class="advup-heroi__selo ficha-pill ficha-pill--neutro"
              >
                {{ t(`${T}.PRODUCAO`) }}
              </span>
              <span
                v-if="retrato.verificado_em"
                class="advup-heroi__selo ficha-pill ficha-pill--neutro"
              >
                {{
                  t(`${T}.VERIFICADO`, {
                    quando: quando(retrato.verificado_em),
                  })
                }}
              </span>
            </div>
          </div>
          <div class="ide-heroi__acoes">
            <!-- Sem chave, quem conecta é a porta logo abaixo; aqui só a troca. -->
            <button
              v-if="pedeGuia && retratoDaTela.estado === 'chave_recusada'"
              type="button"
              class="advup-btn-forte reset-base"
              @click="guiaAberto = true"
            >
              <i class="i-ph-plugs-connected" aria-hidden="true" />
              {{ t(`${F}.GUIA.TROCAR_CHAVE`) }}
            </button>
            <button
              v-if="retrato.chave_salva"
              type="button"
              class="reset-base"
              :class="
                passo === 'testar' && !pedeGuia
                  ? 'advup-btn-forte'
                  : 'advup-btn'
              "
              :disabled="!retrato.chave_salva || testando"
              @click="testar()"
            >
              <i class="i-hugeicons-plug-socket" aria-hidden="true" />
              {{ testando ? t(`${T}.ACOES.TESTANDO`) : t(`${T}.ACOES.TESTAR`) }}
            </button>
            <button
              v-if="passo === 'conectar'"
              type="button"
              class="advup-btn-forte reset-base"
              :disabled="conectando"
              @click="conectar()"
            >
              <i class="i-hugeicons-notification-01" aria-hidden="true" />
              {{
                conectando
                  ? t(`${T}.ACOES.CONECTANDO`)
                  : t(`${T}.ACOES.CONECTAR`)
              }}
            </button>
          </div>
        </div>
        <ul v-if="frasesDoTeste.length" class="ide-teste" aria-live="polite">
          <li
            v-for="(frase, i) in frasesDoTeste"
            :key="i"
            class="ide-teste__linha"
            :class="`ide-teste__linha--${frase.tom}`"
          >
            <i
              :class="
                frase.tom === 'teal'
                  ? 'i-ph-check-circle-fill'
                  : 'i-ph-warning-circle-fill'
              "
              aria-hidden="true"
            />
            {{ frase.texto }}
          </li>
        </ul>
      </section>

      <!-- ── A porta: dois caminhos, para quem ainda não conectou ───────── -->
      <section
        v-if="ehAsaas && retratoDaTela.estado === 'sem_chave'"
        class="ide-porta"
        :aria-label="t(`${F}.GUIA.PORTA_ARIA`)"
      >
        <article
          class="advup-card ide-porta__cartao ide-porta__cartao--principal"
        >
          <span class="ide-porta__ico" aria-hidden="true">
            <i class="i-ph-plugs-connected-fill" />
          </span>
          <h3 class="ide-porta__tit">{{ t(`${F}.GUIA.JA_TENHO_TIT`) }}</h3>
          <p class="ide-porta__dica">{{ t(`${F}.GUIA.JA_TENHO_DICA`) }}</p>
          <ul class="ide-porta__ganhos">
            <li>
              <i class="i-ph-receipt-fill" aria-hidden="true" />
              {{ t(`${F}.GUIA.GANHO_1`) }}
            </li>
            <li>
              <i class="i-ph-check-square-offset-fill" aria-hidden="true" />
              {{ t(`${F}.GUIA.GANHO_2`) }}
            </li>
            <li>
              <i class="i-ph-lock-key-fill" aria-hidden="true" />
              {{ t(`${F}.GUIA.GANHO_3`) }}
            </li>
          </ul>
          <button
            type="button"
            class="advup-btn-forte reset-base ide-porta__acao"
            @click="guiaAberto = true"
          >
            {{ t(`${F}.GUIA.ABRIR`) }}
            <i class="i-ph-arrow-right-bold" aria-hidden="true" />
          </button>
        </article>
        <article class="advup-card ide-porta__cartao">
          <span
            class="ide-porta__ico ide-porta__ico--neutro"
            aria-hidden="true"
          >
            <i class="i-ph-user-plus-fill" />
          </span>
          <h3 class="ide-porta__tit">{{ t(`${F}.GUIA.NAO_TENHO_TIT`) }}</h3>
          <p class="ide-porta__dica">{{ t(`${F}.GUIA.NAO_TENHO_DICA`) }}</p>
          <div class="ide-porta__tarifas">
            <p class="advup-rotulo">{{ t(`${F}.GUIA.TARIFA.TIT`) }}</p>
            <ul>
              <li v-for="linha in tarifas" :key="linha.chave">
                <span>{{ t(`${F}.GUIA.TARIFA.${linha.chave}`) }}</span>
                <strong>{{ linha.valor }}</strong>
              </li>
            </ul>
            <p class="advup-nota">
              {{
                t(`${F}.GUIA.TARIFA.PROMO`, {
                  valor: promo.valor,
                  meses: promo.meses,
                })
              }}
              {{ t(`${F}.GUIA.TARIFA.FONTE`, { data: CONFERIDO_EM }) }}
              <a
                :href="FONTE_DAS_TARIFAS"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ t(`${F}.GUIA.TARIFA.VER_TABELA`) }}
              </a>
            </p>
          </div>
          <a
            :href="SITE_DO_ASAAS"
            target="_blank"
            rel="noopener noreferrer"
            class="advup-btn advup-btn--vazado reset-base ide-porta__acao"
          >
            {{ t(`${F}.GUIA.CRIAR_CONTA`) }}
            <i class="i-ph-arrow-square-out" aria-hidden="true" />
          </a>
        </article>
      </section>

      <!-- ── Com o que liga ────────────────────────────────────────────── -->
      <section class="advup-card ide-card">
        <div class="advup-card__cabeca">
          <h2 class="advup-card__tit">{{ t(`${T}.LIGA_COM.TIT`) }}</h2>
        </div>
        <div class="ide-corpo">
          <p class="advup-nota ide-card__nota">{{ t(`${T}.LIGA_COM.DICA`) }}</p>
          <ul class="advup-linha__lista ide-liga__lista">
            <li
              v-for="ligacao in lista"
              :key="ligacao.chave"
              class="advup-linha ide-liga"
              :class="{ 'ide-liga--apagada': !ligacao.ativa }"
            >
              <span class="ide-liga__ico" aria-hidden="true">
                <i :class="ligacao.icone" />
              </span>
              <span class="ide-liga__txt">
                <span class="ide-liga__tit">
                  {{ t(`${F}.LIGA.${ligacao.chave}_TIT`) }}
                  <span v-if="!ligacao.ativa" class="ide-liga__off">
                    · {{ t(`${T}.LIGA_COM.DESLIGADA`) }}
                  </span>
                </span>
                <span class="ide-liga__dica">
                  {{ t(`${F}.LIGA.${ligacao.chave}_DICA`) }}
                </span>
              </span>
              <router-link
                :to="destino(ligacao)"
                class="advup-btn advup-btn--compacto ide-liga__abrir"
              >
                {{ t(`${T}.ACOES.ABRIR`) }}
                <i class="i-hugeicons-arrow-right-01" aria-hidden="true" />
              </router-link>
            </li>
          </ul>
        </div>
      </section>

      <!-- ── Configuração ──────────────────────────────────────────────── -->
      <form class="advup-card ide-card" @submit.prevent="salvar()">
        <div class="advup-card__cabeca">
          <h2 class="advup-card__tit">{{ t(`${T}.CONFIG.TIT`) }}</h2>
        </div>

        <div class="ide-corpo">
          <div class="ide-campos">
            <div class="ide-campo ide-campo--inteiro">
              <label class="advup-rotulo" :for="`ide-chave-${fornecedor}`">
                {{ t(`${T}.CONFIG.CHAVE`) }}
              </label>
              <!-- Senha que NÃO é a de quem está logado: `new-password` impede o
                   navegador de despejar aqui a credencial do advogado. -->
              <div class="ide-chave">
                <input
                  :id="`ide-chave-${fornecedor}`"
                  v-model="
                    form[ehAsaas ? 'asaas_api_key' : 'tramitacao_api_key']
                  "
                  :type="mostrarChave ? 'text' : 'password'"
                  autocomplete="new-password"
                  data-1p-ignore
                  data-lpignore="true"
                  :name="`advup-${fornecedor}-nao-e-login`"
                  class="advup-campo advup-campo--olho reset-base"
                />
                <button
                  type="button"
                  class="advup-acao advup-acao--p ide-chave__olho reset-base"
                  :aria-label="
                    mostrarChave
                      ? t(`${T}.CONFIG.ESCONDER`)
                      : t(`${T}.CONFIG.MOSTRAR`)
                  "
                  @click="mostrarChave = !mostrarChave"
                >
                  <i
                    :class="
                      mostrarChave ? 'i-hugeicons-view-off' : 'i-hugeicons-view'
                    "
                    aria-hidden="true"
                  />
                </button>
              </div>
              <p class="advup-nota">
                <template v-if="retrato.chave_salva">
                  {{ t(`${T}.CONFIG.CHAVE_MANTER`) }}
                </template>
                {{ t(`${F}.CAMPOS.CHAVE_DICA`) }}
              </p>
            </div>

            <!-- Asaas -->
            <template v-if="ehAsaas">
              <button
                type="button"
                class="advup-chave ide-campo--inteiro reset-base"
                role="switch"
                :aria-checked="String(!!form.asaas_enabled)"
                @click="form.asaas_enabled = !form.asaas_enabled"
              >
                <span class="advup-chave__trilho" aria-hidden="true" />
                <span class="advup-chave__txt">
                  <span class="advup-chave__nome">{{
                    t(`${F}.CAMPOS.LIGADA_NOME`)
                  }}</span>
                  <span class="advup-chave__sub">{{
                    t(`${F}.CAMPOS.LIGADA_DICA`)
                  }}</span>
                </span>
              </button>

              <div class="ide-campo">
                <span class="advup-rotulo">{{ t(`${F}.CAMPOS.FORMA`) }}</span>
                <div
                  class="advup-seg self-start"
                  role="radiogroup"
                  :aria-label="t(`${F}.CAMPOS.FORMA`)"
                >
                  <button
                    v-for="forma in ['pix', 'boleto']"
                    :key="forma"
                    type="button"
                    role="radio"
                    class="advup-seg__btn reset-base"
                    :class="{
                      'advup-seg__btn--on': form.default_billing_type === forma,
                    }"
                    :aria-checked="String(form.default_billing_type === forma)"
                    @click="form.default_billing_type = forma"
                  >
                    <i
                      :class="
                        forma === 'pix' ? 'i-ph-pix-logo' : 'i-ph-barcode'
                      "
                      aria-hidden="true"
                    />
                    {{ t(`${F}.CAMPOS.${forma.toUpperCase()}`) }}
                  </button>
                </div>
              </div>

              <div class="ide-campo ide-encargos">
                <div class="ide-campo">
                  <label class="advup-rotulo" for="ide-juros">{{
                    t(`${F}.CAMPOS.JUROS`)
                  }}</label>
                  <div class="advup-afixo">
                    <input
                      id="ide-juros"
                      v-model="form.default_interest_percent"
                      type="text"
                      inputmode="decimal"
                      class="advup-campo reset-base advup-afixo__campo advup-afixo__campo--fim"
                    />
                    <span
                      class="advup-afixo__txt advup-afixo__txt--fim"
                      aria-hidden="true"
                      >{{ PERCENTUAL }}</span
                    >
                  </div>
                </div>
                <div class="ide-campo">
                  <label class="advup-rotulo" for="ide-multa">{{
                    t(`${F}.CAMPOS.MULTA`)
                  }}</label>
                  <div class="advup-afixo">
                    <input
                      id="ide-multa"
                      v-model="form.default_fine_percent"
                      type="text"
                      inputmode="decimal"
                      class="advup-campo reset-base advup-afixo__campo advup-afixo__campo--fim"
                    />
                    <span
                      class="advup-afixo__txt advup-afixo__txt--fim"
                      aria-hidden="true"
                      >{{ PERCENTUAL }}</span
                    >
                  </div>
                </div>
                <p class="advup-nota ide-campo--inteiro">
                  {{ t(`${F}.CAMPOS.ENCARGOS_DICA`) }}
                </p>
              </div>
            </template>

            <!-- Tramitação Inteligente -->
            <template v-else>
              <div class="ide-campo ide-campo--inteiro">
                <label class="advup-rotulo" for="ide-dono">{{
                  t(`${F}.CAMPOS.DONO`)
                }}</label>
                <input
                  id="ide-dono"
                  v-model="form.assinaturas_dono_email"
                  type="email"
                  autocomplete="off"
                  class="advup-campo reset-base"
                  :placeholder="t(`${F}.CAMPOS.DONO_EXEMPLO`)"
                />
                <p class="advup-nota">{{ t(`${F}.CAMPOS.DONO_DICA`) }}</p>
              </div>
              <button
                type="button"
                class="advup-chave advup-chave--ambar ide-campo--inteiro reset-base"
                role="switch"
                :aria-checked="String(!!form.tramitacao_enabled)"
                @click="form.tramitacao_enabled = !form.tramitacao_enabled"
              >
                <span class="advup-chave__trilho" aria-hidden="true" />
                <span class="advup-chave__txt">
                  <span class="advup-chave__nome">{{
                    t(`${F}.CAMPOS.FONTE_NOME`)
                  }}</span>
                  <span class="advup-chave__sub">{{
                    t(`${F}.CAMPOS.FONTE_DICA`)
                  }}</span>
                </span>
              </button>
            </template>
          </div>

          <div class="ide-rodape">
            <button
              type="submit"
              class="advup-btn-forte reset-base"
              :disabled="salvando"
            >
              {{ salvando ? t(`${T}.ACOES.SALVANDO`) : t(`${T}.ACOES.SALVAR`) }}
            </button>
          </div>
        </div>
      </form>
    </template>
    <ConectarAsaas
      v-if="ehAsaas"
      :aberto="guiaAberto"
      @fechar="guiaAberto = false"
      @conectado="aoConectar"
    />
  </div>
</template>

<style scoped>
.ide {
  display: flex;
  flex-direction: column;
  gap: var(--esp-5);
  width: 100%;
  padding-bottom: var(--esp-6);
}

.ide > * {
  flex-shrink: 0;
}

/* ── Herói ── */
.ide-heroi__frase {
  text-wrap: balance;
}

.ide-heroi__acoes {
  display: flex;
  flex-wrap: wrap;
  gap: var(--esp-2);
  align-items: center;
  margin-left: auto;
}

.ide-teste {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  margin: var(--esp-4) 0 0;
  padding: var(--esp-3) var(--esp-4);
  list-style: none;
  border-radius: var(--r-3);
  background: rgb(var(--solid-1) / 0.7);
}

.ide-teste__linha {
  display: flex;
  gap: var(--esp-2);
  align-items: flex-start;
  font-size: var(--txt-corpo);
  line-height: var(--linha-normal);
  color: rgb(var(--slate-12));
}

.ide-teste__linha > i {
  flex: none;
  width: var(--ico-2);
  height: var(--ico-2);
  margin-top: calc(var(--esp-1) / 2);
}

.ide-teste__linha--teal > i {
  color: rgb(var(--teal-11));
}

.ide-teste__linha--ambar > i {
  color: rgb(var(--amber-11));
}

.ide-teste__linha--rubi > i {
  color: rgb(var(--ruby-11));
}

/* ── Cartões ── */
/* O cartão do padrão não tem recuo: a cabeça é uma faixa de borda a borda e o
   corpo tem o recuo dele (mesma conta de `.advup-card__cabeca`). */
.ide-corpo {
  padding: var(--esp-4) var(--esp-5) var(--esp-5);
}

.ide-card__nota {
  margin: 0 0 var(--esp-3);
}

/* ── Com o que liga ── */
/* A linha tem recuo próprio (o véu do cursor precisa de borda); a lista sai
   para fora o mesmo tanto, e o ícone alinha com a frase de ajuda. */
.ide-liga__lista {
  margin-inline: calc(var(--esp-3) * -1);
}

.ide-liga {
  display: flex;
  gap: var(--esp-3);
  align-items: center;
  padding: var(--esp-3);
}

.ide-liga__ico {
  display: grid;
  flex: none;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: var(--r-3);
  color: rgb(var(--blue-11));
  background: rgb(var(--blue-3));
  transition:
    color var(--mov-1) var(--mov-curva),
    background-color var(--mov-1) var(--mov-curva);
}

.ide-liga__ico > i {
  width: var(--ico-3);
  height: var(--ico-3);
}

.ide-liga__txt {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.ide-liga__tit {
  font-size: var(--txt-bloco);
  font-weight: var(--peso-forte);
  line-height: var(--linha-normal);
  color: rgb(var(--slate-12));
}

.ide-liga__off {
  font-size: var(--txt-nota);
  font-weight: var(--peso-leve);
  color: rgb(var(--slate-11));
}

.ide-liga__dica {
  font-size: var(--txt-nota);
  line-height: var(--linha-solta);
  color: rgb(var(--slate-11));
}

/* O que ainda não está ligado fica no lugar, apagado: a pessoa vê o que
   ganharia ligando. Ícone cinza, texto continua legível (slate-11). */
.ide-liga--apagada .ide-liga__ico {
  color: rgb(var(--slate-10));
  background: rgb(var(--slate-3));
}

.ide-liga__abrir {
  flex: none;
}

/* ── Configuração ── */
.ide-campos {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--esp-4);
}

.ide-campo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  min-width: 0;
}

.ide-campo--inteiro {
  grid-column: 1 / -1;
}

.ide-encargos {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--esp-3);
}

.ide-chave {
  position: relative;
}

.ide-chave__olho {
  position: absolute;
  top: 50%;
  right: var(--esp-1);
  transform: translateY(-50%);
}

.ide-rodape {
  display: flex;
  justify-content: flex-end;
  margin-top: var(--esp-5);
  /* A bolha do assistente mora no canto e não se move (Victor, 07/09): o
     rodapé abre espaço para ela. */
  padding-inline-end: 80px;
}

/* ── Celular: bloco no fim, como pede o padrão ── */
/* A PORTA (11/10/2026): dois caminhos lado a lado para quem ainda não
   conectou — a principal com o ladrilho da marca, a outra neutra. */
.ide-porta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--esp-4);
}

.ide-porta__cartao {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--esp-2);
  padding: var(--esp-5);
}

.ide-porta__cartao--principal {
  box-shadow:
    0 0 0 1px rgb(var(--blue-9) / 0.35),
    0 6px 20px -12px rgb(var(--blue-9) / 0.6);
}

.ide-porta__ico {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  margin-bottom: var(--esp-1);
  border-radius: var(--r-3);
  background: rgb(var(--blue-9));
  color: #fff;
  box-shadow: 0 6px 16px -6px rgb(var(--blue-9) / 0.7);
}

.ide-porta__ico--neutro {
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-11));
  box-shadow: none;
}

.ide-porta__ico > i {
  width: var(--ico-3);
  height: var(--ico-3);
  font-size: var(--ico-3);
}

.ide-porta__tit {
  margin: 0;
  font-size: var(--txt-bloco);
  font-weight: var(--peso-forte);
  line-height: var(--linha-normal);
  color: rgb(var(--slate-12));
}

.ide-porta__dica {
  margin: 0;
  font-size: var(--txt-corpo);
  line-height: var(--linha-solta);
  color: rgb(var(--slate-11));
}

.ide-porta__ganhos {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  width: 100%;
  margin: 0;
  padding: var(--esp-3);
  border-radius: var(--r-3);
  background: rgb(var(--blue-2));
  list-style: none;
}

.ide-porta__ganhos > li {
  display: flex;
  align-items: flex-start;
  gap: var(--esp-2);
  font-size: var(--txt-corpo);
  line-height: var(--linha-normal);
  color: rgb(var(--slate-12));
}

.ide-porta__ganhos > li > i {
  flex-shrink: 0;
  width: var(--ico-3);
  height: var(--ico-3);
  font-size: var(--ico-3);
  color: rgb(var(--blue-10));
}

.ide-porta__tarifas {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  width: 100%;
  padding: var(--esp-3);
  border-radius: var(--r-3);
  background: rgb(var(--slate-2));
}

.ide-porta__tarifas > p {
  margin: 0;
}

.ide-porta__tarifas > ul {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  margin: 0;
  padding: 0;
  list-style: none;
}

.ide-porta__tarifas li {
  display: flex;
  justify-content: space-between;
  gap: var(--esp-3);
  font-size: var(--txt-corpo);
  line-height: var(--linha-normal);
  color: rgb(var(--slate-11));
}

.ide-porta__tarifas strong {
  font-weight: var(--peso-forte);
  font-variant-numeric: tabular-nums;
  color: rgb(var(--slate-12));
}

.ide-porta__tarifas a {
  font-weight: var(--peso-forte);
  color: rgb(var(--blue-11));
  text-decoration: underline;
}

.ide-porta__acao {
  margin-top: auto;
}

@media (width < 640px) {
  .ide-porta {
    grid-template-columns: minmax(0, 1fr);
  }

  .ide-heroi__acoes {
    width: 100%;
    margin-left: 0;
  }

  .ide-heroi__acoes > button {
    flex: 1 1 0;
  }

  .ide-campos {
    grid-template-columns: minmax(0, 1fr);
  }

  .ide-liga {
    flex-wrap: wrap;
    align-items: flex-start;
  }

  /* O texto toma a linha inteira ao lado do ícone; o "Abrir" desce. Sem a
     base, ele encolhia até uma coluna de uma palavra por linha. */
  .ide-liga__txt {
    flex-basis: calc(100% - 36px - var(--esp-3));
  }

  .ide-liga__abrir {
    margin-left: calc(36px + var(--esp-3));
  }
}
</style>
